import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Check, MessageSquare, Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { commentsListQueryOptions, useUpdateCommentStatus } from '@/lib/comments';

const PREVIEW_LIMIT = 5;

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`;
  return new Date(iso).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' });
}

/**
 * Pending-moderation queue surfaced on the dashboard for users with
 * comment.moderate. Shows total count + top 5 with inline approve /
 * reject so moderators can clear short queues without leaving home.
 *
 * Each row optimistically disables both buttons while a single
 * mutation is in-flight (mutation is shared across rows because all
 * actions reuse the same `useUpdateCommentStatus` instance — a
 * second row click would race; cheaper to gate than queue).
 */
export function PendingCommentsCard() {
  const query = useQuery(
    commentsListQueryOptions({
      status: 'pending',
      perPage: PREVIEW_LIMIT,
    }),
  );
  const update = useUpdateCommentStatus();

  const items = query.data?.items ?? [];
  const total = query.data?.pagination.total ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4 text-slate-400" />
          คอมเมนต์รออนุมัติ
          {total > 0 && (
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">
              {total}
            </span>
          )}
        </CardTitle>
        <Link
          to="/comments"
          search={{ status: 'pending' }}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดูทั้งหมด →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">ไม่มีคอมเมนต์รออนุมัติ</p>
        ) : (
          <ul className="space-y-3">
            {items.map((c) => (
              <li
                key={c.id}
                className="space-y-1.5 border-b border-slate-100 pb-2 last:border-0 dark:border-slate-800"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                    {c.authorName}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">
                    {formatRelative(c.createdAt)}
                  </span>
                </div>
                <Link
                  to="/posts/$postId"
                  params={{ postId: c.postId }}
                  className="hover:text-brand-600 dark:hover:text-brand-400 block truncate text-[11px] text-slate-500"
                >
                  บน: {c.postTitle}
                </Link>
                <p className="line-clamp-2 text-xs text-slate-600 dark:text-slate-300">{c.body}</p>
                <div className="flex gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: c.id, status: 'approved' })}
                  >
                    <Check className="mr-1 h-3 w-3 text-emerald-600" />
                    อนุมัติ
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: c.id, status: 'rejected' })}
                  >
                    <X className="mr-1 h-3 w-3 text-red-500" />
                    ปฏิเสธ
                  </Button>
                  {/* Spam shortcut — common moderation action that
                      currently required entering the full /comments
                      page. No confirm dialog (reversible status, not a
                      destructive delete). */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: c.id, status: 'spam' })}
                    title="ทำเครื่องหมายเป็นสแปม"
                  >
                    <Shield className="mr-1 h-3 w-3 text-orange-500" />
                    สแปม
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
